export interface CustomAppCloseDecision {
	warning?: string;
	unresponsive?: boolean;
}

/**
 * One lifecycle per iframe. Late replies cannot affect a subsequent close request.
 * Apps that never acknowledged the connection do not use the Aavo SDK, so they cannot answer a close request.
 */
export function createCustomAppLifecycle(send: (message: unknown) => void, timeoutMs = 5000) {
	let pending: { id: string; finish: (decision: CustomAppCloseDecision) => void } | undefined;
	let closing: Promise<CustomAppCloseDecision> | undefined;
	let closed: (() => void) | undefined;
	let disposed = false;
	let acknowledged = false;

	return {
		beforeClose(): Promise<CustomAppCloseDecision> {
			if (disposed || !acknowledged) return Promise.resolve({});
			if (closing) return closing;
			closing = new Promise<CustomAppCloseDecision>((resolve) => {
				const id = crypto.randomUUID();
				const timer = setTimeout(() => pending?.finish({ unresponsive: true }), timeoutMs);
				pending = {
					id,
					finish(decision) {
						clearTimeout(timer);
						pending = undefined;
						resolve(decision);
					},
				};
				try {
					send({ type: "aavo:custom-app:before-close", requestId: id });
				} catch {
					pending.finish({ unresponsive: true });
				}
			}).finally(() => {
				closing = undefined;
			});
			return closing;
		},
		async notifyClosed(): Promise<void> {
			if (disposed || !acknowledged) return;
			await new Promise<void>((resolve) => {
				const timer = setTimeout(finish, 250);
				function finish() {
					clearTimeout(timer);
					closed = undefined;
					resolve();
				}
				closed = finish;
				try {
					send({ type: "aavo:custom-app:closed" });
				} catch {
					finish();
				}
			});
		},
		handleMessage(message: unknown): boolean {
			if (typeof message !== "object" || message === null || !("type" in message)) return false;
			if (message.type === "aavo:custom-app:connected") {
				acknowledged = true;
				return true;
			}
			if (message.type === "aavo:custom-app:closed-ack") {
				closed?.();
				return true;
			}
			if (message.type !== "aavo:custom-app:before-close-result") return false;
			if (!pending || !("requestId" in message) || pending.id !== message.requestId) return true;
			if ("failed" in message && message.failed === true) pending.finish({ unresponsive: true });
			else if (!("warning" in message) || message.warning == null) pending.finish({});
			else if (typeof message.warning === "string" && message.warning.length <= 4000) {
				pending.finish({ warning: message.warning });
			} else pending.finish({ unresponsive: true });
			return true;
		},
		dispose() {
			disposed = true;
			pending?.finish({});
			closed?.();
		},
	};
}
